import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircleIcon, XIcon } from 'lucide-react';
const videos = [{
  id: '71f4e2149a654b0c9f643e9205f2def1',
  title: 'Purchase Orders and Receiving',
  description: 'Create purchase orders, track vendor deliveries, and process receiving with quality inspections and inventory updates.'
}, {
  id: '408df635aa6b4d8e83bac62b61701c92',
  title: 'Deal Initiation and Project Creation',
  description: 'Initiate new deals, create project records, and set up the foundational structure for project management.'
}, {
  id: '191536141dae4f5c8eb363c768dc8b2c',
  title: 'Project Planning',
  description: 'Plan project timelines, allocate resources, define milestones, and establish deliverables for successful execution.'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
export function DemoVideosPage() {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (selectedVideo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedVideo]);
  return <div className="min-h-screen bg-navy text-white font-sans selection:bg-violet-electric/30 pt-32 pb-20" data-id="element-1125" data-mp="36fsvwahx2gix">
      <div className="max-w-7xl mx-auto px-6" data-id="element-1126" data-mp="3agl5x5ym4pg9">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-1127" data-mp="2wk7xemc23ydx">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-violet-electric text-sm font-semibold tracking-widest uppercase mb-6 block" data-id="element-1128" data-mp="31hhl9xjss1zn">

            Demo Videos
          </motion.span>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold mb-8" data-id="element-1129" data-mp="y9h7bhujhq61">

            See Thruline in action
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-xl text-white/60 leading-relaxed mb-6" data-id="element-1130" data-mp="jcghxkztm7aq">

            Watch quick demos of Thruline's core capabilities — from production
            tracking to quality management.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25
        }} className="text-sm text-white/40 italic" data-id="element-1131" data-mp="12b2h2ubs1oc0">

            Note: This is a subset of our functionality.{' '}
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="text-violet-electric hover:underline" data-id="element-1132" data-mp="1uxwzllp1admu">

              Contact us
            </a>{' '}
            for the full set.
          </motion.p>
        </div>

        {/* Video Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 max-w-4xl mx-auto" variants={stagger} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} data-id="element-1133" data-mp="2c7hg1acit1n0">

          {videos.map(video => <motion.button key={video.id} variants={fadeUp} onClick={() => setSelectedVideo(video.id)} className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.03] overflow-hidden hover:border-violet-electric/30 hover:bg-white/[0.05] transition-all duration-300 text-left" data-id="element-1134" data-mp="2q8lj3tvk5xxp">

              {/* Video Thumbnail with Embedded Preview */}
              <div className="relative aspect-video bg-white/[0.02] overflow-hidden" data-id="element-1135" data-mp="34zknespd20bh">
                <iframe src={`https://www.loom.com/embed/${video.id}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true&autoplay=false`} frameBorder="0" className="w-full h-full pointer-events-none" style={{
              pointerEvents: 'none'
            }} data-id="element-1136" data-mp="39xcddh6g3cd1" />

                <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" data-id="element-1137" data-mp="k7ldesw4y452" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20" data-id="element-1138" data-mp="f9a6or1509r2">
                  <div className="w-16 h-16 rounded-full bg-violet-electric/30 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1139" data-mp="3nr6l0c2v4ine">
                    <PlayCircleIcon className="w-8 h-8 text-white" data-id="element-1140" data-mp="m76gclfjn01" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6" data-id="element-1141" data-mp="1bz1a5frep790">
                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-violet-electric transition-colors" data-id="element-1142" data-mp="1by82mckmidpt">
                  {video.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-1143" data-mp="1fgoxoy5ehhbq">
                  {video.description}
                </p>
              </div>
            </motion.button>)}
        </motion.div>
      </div>

      {/* Video Modal */}
      <AnimatePresence data-id="element-1144" data-mp="15ldhsci9a42e">
        {selectedVideo && <>
            {/* Backdrop */}
            <motion.div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSelectedVideo(null)} data-id="element-1145" data-mp="1aj5k80gj18ku" />


            {/* Modal */}
            <motion.div className="fixed inset-0 z-[101] flex items-center justify-center p-4 sm:p-6 pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-1146" data-mp="1ohdaxqmyzdnk">

              <motion.div className="relative w-full max-w-5xl pointer-events-auto" initial={{
            scale: 0.95,
            y: 20
          }} animate={{
            scale: 1,
            y: 0
          }} exit={{
            scale: 0.95,
            y: 20
          }} transition={{
            duration: 0.25,
            ease: [0.16, 1, 0.3, 1]
          }} onClick={e => e.stopPropagation()} data-id="element-1147" data-mp="2lxsl7sb880p5">

                {/* Close button */}
                <button onClick={() => setSelectedVideo(null)} className="absolute -top-12 right-0 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors" aria-label="Close video" data-id="element-1148" data-mp="2hsyivqn6x5v3">

                  <XIcon className="w-6 h-6 text-white" data-id="element-1149" data-mp="283ydnpi3he8g" />
                </button>

                {/* Video Container */}
                <div className="relative rounded-2xl overflow-hidden bg-navy border border-white/10 shadow-2xl" data-id="element-1150" data-mp="235n65lf9vfte">
                  <div className="aspect-video" data-id="element-1151" data-mp="1z5d0yazb0tfl">
                    <iframe src={`https://www.loom.com/embed/${selectedVideo}?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true`} frameBorder="0" allowFullScreen className="w-full h-full" data-id="element-1152" data-mp="1u4yqkma8d7ee" />

                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}